/**
 * Google OIDC OAuth client IDs, keyed by build environment.
 *
 * These are the same client IDs the Ember app baked into `config/environment.js`
 * per `CIO_ENV`. The environment buckets mirror `services/api/base-url.ts`:
 * `production` → prod app, `standalone` → standalone-staging app, everything
 * else → the dev app (usable only with @customer.io GSuite accounts).
 *
 * The same Google OAuth app backs both SSO Google OIDC and Google One-Click —
 * there is no separate One-Click client ID.
 */
const GOOGLE_OIDC_CLIENT_IDS = {
  // Production app: console project abiding-bongo-268710.
  production: '824898409636-7u6i29phiu6fis003o5uqho8k9480dcp.apps.googleusercontent.com',
  // Standalone-staging app: console project strong-atlas-269407.
  standalone: '824898409636-q7rkclijsm8en7elohu82625dpeao91q.apps.googleusercontent.com',
  // Dev app: console project abiding-bongo-268710, @customer.io accounts only.
  development: '824898409636-5uifj4lcu9hh8cdddol64iduhqr44npd.apps.googleusercontent.com',
} as const;

export function getGoogleOidcClientId(): string {
  // An isolated environment keeps __CIO_ENV__ = 'standalone' (see
  // utils/api-host-override.ts), so it would otherwise use the standalone-staging
  // app, whose authorized origins do not include its host — Google rejects the
  // sign-in with redirect_uri_mismatch. Its own client id is supplied at build
  // time rather than listed above, so adding an environment does not need an app
  // code change. Client ids are public; the matching secret is server-side.
  const clientIdOverride = (import.meta.env.VITE_GOOGLE_OIDC_CLIENT_ID ?? '').trim();
  if (clientIdOverride !== '') {
    return clientIdOverride;
  }

  if (__CIO_ENV__ === 'production') {
    return GOOGLE_OIDC_CLIENT_IDS.production;
  }

  if (__CIO_ENV__ === 'standalone') {
    return GOOGLE_OIDC_CLIENT_IDS.standalone;
  }

  return GOOGLE_OIDC_CLIENT_IDS.development;
}
